import { apiGet, apiSend } from "@/lib/api";

export const BEAUTY_PRESET_LANGUAGES = ["zh", "en", "ja", "ko", "de", "es", "fr", "pt", "it", "ru"] as const;
export type BeautyPresetLanguage = (typeof BEAUTY_PRESET_LANGUAGES)[number];

export interface BeautyParameters {
  smoothing: number;
  whiteness: number;
  slimFace: number;
  bigEyes: number;
  blusher: number;
  lipstick: number;
  exposure: number;
  contrast: number;
  saturation: number;
  beautyMaskEnabled: boolean;
}

export interface BeautyPreset {
  name: string;
  i18n: Record<string, { name: string }>;
  parameters: BeautyParameters;
  isDefault: boolean;
}

export interface AdminBeautyPreset extends BeautyPreset {
  id: string;
  enabled: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface BeautyPresetCatalog {
  schemaVersion: 1;
  revision: number;
  presets: BeautyPreset[];
}

export interface AdminBeautyPresetCatalog {
  schemaVersion: 1;
  revision: number;
  presets: AdminBeautyPreset[];
}

export type BeautyPresetWrite = Pick<BeautyPreset, "name" | "i18n" | "parameters" | "isDefault"> & {
  enabled: boolean;
};

export type BeautyPresetPatch = Partial<BeautyPresetWrite>;
export interface BeautyPresetNameTranslationResponse {
  i18n: Record<string, { name: string }>;
}


function parseBeautyPresetNameTranslation(value: unknown, langs: string[]): BeautyPresetNameTranslationResponse {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error("服务器返回的翻译结果格式无效，请重试");
  }
  const envelope = value as Record<string, unknown>;
  if (typeof envelope.i18n !== "object" || envelope.i18n === null || Array.isArray(envelope.i18n)) {
    throw new Error("服务器返回的翻译结果格式无效，请重试");
  }
  const translated = envelope.i18n as Record<string, unknown>;
  const requested = new Set(langs);
  const keys = Object.keys(translated);
  if (keys.length !== langs.length || keys.some((language) => !requested.has(language))) {
    throw new Error("服务器返回的翻译语言不完整，请重试");
  }
  const i18n: Record<string, { name: string }> = {};
  for (const language of langs) {
    const valueForLanguage = translated[language];
    if (typeof valueForLanguage !== "object" || valueForLanguage === null || Array.isArray(valueForLanguage)) {
      throw new Error("服务器返回的翻译结果格式无效，请重试");
    }
    const translatedName = (valueForLanguage as Record<string, unknown>).name;
    if (typeof translatedName !== "string" || !translatedName.trim()) {
      throw new Error("服务器返回了空的翻译名称，请重试");
    }
    i18n[language] = { name: translatedName.trim() };
  }
  return { i18n };
}


export const DEFAULT_BEAUTY_PARAMETERS: BeautyParameters = {
  smoothing: 0,
  whiteness: 0,
  slimFace: 0,
  bigEyes: 0,
  blusher: 0,
  lipstick: 0,
  exposure: 0,
  contrast: 0,
  saturation: 0,
  beautyMaskEnabled: true,
};
export async function translateBeautyPresetName(name: string, langs: string[]): Promise<BeautyPresetNameTranslationResponse> {
  const trimmedName = typeof name === "string" ? name.trim() : "";
  if (!trimmedName) throw new Error("中文基础名称不能为空");
  if (!Array.isArray(langs) || langs.length === 0) throw new Error("请至少选择一种翻译语言");
  const requestedLangs = [...langs];
  const unique = new Set<string>();
  for (const language of requestedLangs) {
    if (
      typeof language !== "string"
      || language === "zh"
      || !BEAUTY_PRESET_LANGUAGES.includes(language as BeautyPresetLanguage)
      || unique.has(language)
    ) {
      throw new Error("翻译语言参数无效");
    }
    unique.add(language);
  }
  const response = await apiSend<unknown>("POST", "/admin/beauty-presets/translate-name", {
    name: trimmedName,
    langs: requestedLangs,
  });
  return parseBeautyPresetNameTranslation(response, requestedLangs);
}
export const BEAUTY_PARAMETER_LIMITS: Record<
  Exclude<keyof BeautyParameters, "beautyMaskEnabled">,
  readonly [number, number]
> = {
  smoothing: [0, 100],
  whiteness: [0, 100],
  slimFace: [0, 100],
  bigEyes: [0, 100],
  blusher: [0, 100],
  lipstick: [0, 100],
  exposure: [-100, 100],
  contrast: [-100, 100],
  saturation: [-100, 100],
};

export function getBeautyPresetCatalog(signal?: AbortSignal): Promise<BeautyPresetCatalog> {
  return apiGet<BeautyPresetCatalog>("/beauty-presets", signal);
}

export function getAdminBeautyPresetCatalog(signal?: AbortSignal): Promise<AdminBeautyPresetCatalog> {
  return apiGet<AdminBeautyPresetCatalog>("/admin/beauty-presets", signal);
}

export function createBeautyPreset(body: BeautyPresetWrite): Promise<AdminBeautyPresetCatalog> {
  return apiSend<AdminBeautyPresetCatalog>("POST", "/admin/beauty-presets", body);
}

export function updateBeautyPreset(id: string, body: BeautyPresetPatch): Promise<AdminBeautyPresetCatalog> {
  return apiSend<AdminBeautyPresetCatalog>("PATCH", `/admin/beauty-presets/${encodeURIComponent(id)}`, body);
}

export function deleteBeautyPreset(id: string): Promise<AdminBeautyPresetCatalog> {
  return apiSend<AdminBeautyPresetCatalog>("DELETE", `/admin/beauty-presets/${encodeURIComponent(id)}`);
}

export function reorderBeautyPresets(ids: string[]): Promise<AdminBeautyPresetCatalog> {
  return apiSend<AdminBeautyPresetCatalog>("POST", "/admin/beauty-presets/reorder", { ids });
}
